if (!customElements.get('contact-lens-cost-comparison')) {
  customElements.define('contact-lens-cost-comparison', class ContactLensCostComparison extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.storageKey = this.dataset.storageKey || 'city-contact-cost-view-v1';
      this.frequencyStorageKey = this.dataset.frequencyStorageKey || 'city-contact-cost-frequency-v1';
      this.allowedViews = new Set(['box', '3_month', '6_month']);
      this.allowedFrequencies = new Set(['7', '5', '3', '1']);
      this.sortDataCache = new Map();
      this.sortRequestId = 0;
      this.sortFrame = 0;

      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="radio"]');
        if (input && this.allowedViews.has(input.value)) {
          this.setView(input.value, true);
          return;
        }

        const frequency = event.target.closest('[data-contact-cost-frequency]');
        if (frequency && this.allowedFrequencies.has(frequency.value)) {
          this.setFrequency(frequency.value, true);
        }
      });

      this.infoToggle = this.querySelector('[data-contact-cost-info-toggle]');
      this.infoPanel = this.querySelector('[data-contact-cost-info]');
      this.infoToggle?.addEventListener('click', () => this.toggleInfo());

      this.frequencySelect = this.querySelector('[data-contact-cost-frequency]');
      this.frequencyControl = this.querySelector('[data-contact-cost-frequency-control]');
      this.noteCopy = this.querySelector('[data-contact-cost-note-copy]');
      this.setFrequency(this.readStoredFrequency(), false, false);
      const storedView = this.readStoredView();
      this.setView(storedView, false);

      this.observer = new MutationObserver((mutations) => {
        const hasNewCards = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some((node) =>
            node.nodeType === Node.ELEMENT_NODE
            && (node.matches?.('[data-contact-cost-card]') || node.querySelector?.('[data-contact-cost-card]'))
          )
        );
        if (hasNewCards) {
          this.updateCards(this.currentView);
          this.syncPriceSort();
        }
      });
      const gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) this.observer.observe(gridContainer, { childList: true, subtree: true });
      this.syncPriceSort();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.sortFrame) window.cancelAnimationFrame(this.sortFrame);
    }

    readStoredView() {
      try {
        const stored = window.localStorage.getItem(this.storageKey);
        return this.allowedViews.has(stored) ? stored : 'box';
      } catch (error) {
        return 'box';
      }
    }

    readStoredFrequency() {
      try {
        const stored = window.localStorage.getItem(this.frequencyStorageKey);
        return this.allowedFrequencies.has(stored) ? stored : '7';
      } catch (error) {
        return '7';
      }
    }

    setView(view, userInitiated) {
      this.currentView = this.allowedViews.has(view) ? view : 'box';
      this.dataset.view = this.currentView;
      if (this.currentView === 'box') this.setSortError(false);
      if (this.frequencyControl) this.frequencyControl.hidden = this.currentView === 'box';
      this.setText(
        this.noteCopy,
        this.currentView === 'box'
          ? 'View the current price of each available contact lens pack.'
          : 'Based on both eyes. Daily lens estimates adjust to your wearing frequency; reusable lenses follow their replacement schedule.'
      );
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = input.value === this.currentView;
      });

      if (userInitiated) {
        try {
          window.localStorage.setItem(this.storageKey, this.currentView);
        } catch (error) {
          // Browsing still works when storage is blocked.
        }
        this.track('contact_cost_compare_view_changed', { price_view: this.currentView });
        const status = this.querySelector('[data-contact-cost-status]');
        const statusText = this.currentView === 'box'
          ? 'Product cards now show box prices.'
          : `Product cards now show estimated ${this.currentView === '3_month' ? '3-month' : '6-month'} costs for both eyes at ${this.daysPerWeek} days per week.`;
        this.setText(status, statusText);
      }

      this.updateCards(this.currentView);
      this.syncPriceSort();
    }

    toggleInfo() {
      if (!this.infoToggle || !this.infoPanel) return;
      const willOpen = this.infoToggle.getAttribute('aria-expanded') !== 'true';
      this.infoToggle.setAttribute('aria-expanded', String(willOpen));
      this.infoPanel.hidden = !willOpen;
      if (willOpen) this.track('contact_cost_compare_info_opened');
    }

    setFrequency(value, userInitiated, updateCards = true) {
      const frequency = this.allowedFrequencies.has(String(value)) ? String(value) : '7';
      this.daysPerWeek = Number(frequency);
      if (this.frequencySelect) this.frequencySelect.value = frequency;

      if (userInitiated) {
        try {
          window.localStorage.setItem(this.frequencyStorageKey, frequency);
        } catch (error) {
          // Browsing still works when storage is blocked.
        }
        this.track('contact_cost_wearing_frequency_changed', { days_per_week: this.daysPerWeek });
        const status = this.querySelector('[data-contact-cost-status]');
        const statusText = this.currentView === 'box'
          ? `Wearing frequency set to ${this.daysPerWeek} ${this.daysPerWeek === 1 ? 'day' : 'days'} per week. It will be used for daily-lens estimates.`
          : `Daily lens estimates now use ${this.daysPerWeek} ${this.daysPerWeek === 1 ? 'day' : 'days'} per week. Reusable lens estimates still follow their replacement schedule.`;
        this.setText(status, statusText);
      }

      if (updateCards) {
        this.updateCards(this.currentView || 'box');
        this.syncPriceSort();
      }
    }

    updateCards(view) {
      document.querySelectorAll('[data-contact-cost-card]').forEach((card) => {
        const boxPrice = card.querySelector('[data-contact-cost-box-price]');
        const comparison = card.querySelector('[data-contact-cost-comparison-price]');
        const unavailable = card.querySelector('[data-contact-cost-unavailable]');
        const basis = card.querySelector('[data-contact-cost-basis]');
        if (!boxPrice || !comparison) return;

        const basisText = card.dataset.costComparisonSchedule === 'daily'
          ? `Both eyes · ${this.daysPerWeek} ${this.daysPerWeek === 1 ? 'day' : 'days'}/week`
          : 'Both eyes · replacement schedule';
        this.setText(basis, basisText);

        if (view === 'box') {
          boxPrice.hidden = false;
          comparison.hidden = true;
          if (unavailable) unavailable.hidden = true;
          return;
        }

        const calculation = this.calculateComparison(card.dataset, view);
        if (!calculation) {
          boxPrice.hidden = false;
          comparison.hidden = true;
          if (unavailable) unavailable.hidden = false;
          return;
        }

        const periodLabel = view === '3_month' ? '3 months' : '6 months';
        const dailyLabel = calculation.schedule === 'daily'
          ? `${this.formatMoney(calculation.dailyEquivalentCents)} per wearing day`
          : `${this.formatMoney(calculation.dailyEquivalentCents)}/day equivalent`;
        const prefix = card.dataset.costComparisonMultiplePacks === 'true' ? 'Est. from' : 'Est.';

        this.setText(comparison.querySelector('[data-contact-cost-prefix]'), prefix);
        this.setText(comparison.querySelector('[data-contact-cost-amount]'), this.formatMoney(calculation.periodCostCents));
        this.setText(comparison.querySelector('[data-contact-cost-period]'), `/ ${periodLabel}`);
        this.setText(comparison.querySelector('[data-contact-cost-daily]'), dailyLabel);
        boxPrice.hidden = true;
        comparison.hidden = false;
        if (unavailable) unavailable.hidden = true;
      });
    }

    calculateComparison(data, view) {
      if (view !== '3_month' && view !== '6_month') return null;

      const pricePerLens = Number(data.costComparisonPricePerLens);
      const schedule = data.costComparisonSchedule;
      const supportedSchedules = new Set(['daily', 'weekly', 'fortnightly', 'monthly', 'six_monthly', 'yearly']);
      if (!(pricePerLens > 0) || !supportedSchedules.has(schedule)) return null;

      const periodWeeks = view === '3_month' ? 12 : 24;
      const periodMonths = view === '3_month' ? 3 : 6;
      const periodCalendarDays = periodWeeks * 7;
      let lensRequirement = 0;

      if (schedule === 'daily') lensRequirement = periodWeeks * this.daysPerWeek * 2;
      if (schedule === 'weekly') lensRequirement = periodWeeks * 2;
      if (schedule === 'fortnightly') lensRequirement = (periodWeeks / 2) * 2;
      if (schedule === 'monthly') lensRequirement = periodMonths * 2;
      if (schedule === 'six_monthly') lensRequirement = (periodMonths / 6) * 2;
      if (schedule === 'yearly') lensRequirement = (periodMonths / 12) * 2;

      const periodCostCents = pricePerLens * lensRequirement;
      const dailyEquivalentCents = schedule === 'daily'
        ? pricePerLens * 2
        : periodCostCents / periodCalendarDays;

      return { schedule, lensRequirement, periodCostCents, dailyEquivalentCents };
    }

    getActivePriceSort() {
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      return sort === 'price-ascending' || sort === 'price-descending' ? sort : '';
    }

    getCurrentPage() {
      const current = document.querySelector('.pagination [aria-current="page"]');
      const currentPage = Number(current?.textContent?.trim());
      if (currentPage > 0) return currentPage;
      return Math.max(1, Number(new URLSearchParams(window.location.search).get('page')) || 1);
    }

    getSortCacheKey() {
      const params = new URLSearchParams(window.location.search);
      const resultParams = new URLSearchParams();
      params.forEach((value, key) => {
        if (!value) return;
        if (key === 'view' || key === 'q' || key.startsWith('filter.') || key.startsWith('options[')) {
          resultParams.append(key, value);
        }
      });
      resultParams.sort();
      return `${window.location.pathname}?${resultParams.toString()}`;
    }

    syncPriceSort() {
      const priceSort = this.getActivePriceSort();
      if (!priceSort) return;

      if (this.currentView === 'box') {
        this.restoreNativePriceSort();
        return;
      }

      if (this.sortFrame) window.cancelAnimationFrame(this.sortFrame);
      this.sortFrame = window.requestAnimationFrame(() => {
        this.sortFrame = 0;
        this.applyModeAwarePriceSort(priceSort);
      });
    }

    restoreNativePriceSort() {
      const grid = document.getElementById('product-grid');
      const activeLoading = this.activeSortLoading;
      this.sortRequestId += 1;

      if (
        activeLoading
        && activeLoading.cacheKey === this.getSortCacheKey()
        && activeLoading.page === this.getCurrentPage()
      ) {
        activeLoading.collection?.classList.remove('loading');
        activeLoading.collection?.removeAttribute('aria-busy');
        this.activeSortLoading = null;
      }

      if (!grid?.dataset.contactCostSortSignature || !window.FacetFiltersForm?.renderPage) return;

      const params = new URLSearchParams(window.location.search);
      const currentPage = this.getCurrentPage();
      if (currentPage > 1) params.set('page', String(currentPage));
      else params.delete('page');
      delete grid.dataset.contactCostSortSignature;
      window.FacetFiltersForm.renderPage(params.toString(), null, false);
    }

    async applyModeAwarePriceSort(priceSort) {
      if (this.currentView === 'box' || priceSort !== this.getActivePriceSort()) return;

      const grid = document.getElementById('product-grid');
      const gridContainer = document.getElementById('ProductGridContainer');
      if (!grid || !gridContainer || !grid.dataset.id) return;

      const cacheKey = this.getSortCacheKey();
      const page = this.getCurrentPage();
      const view = this.currentView;
      const daysPerWeek = this.daysPerWeek;
      const signature = `${cacheKey}|${this.currentView}|${this.daysPerWeek}|${priceSort}|${page}`;
      if (grid.dataset.contactCostSortSignature === signature) return;

      const requestId = ++this.sortRequestId;
      const collection = gridContainer.querySelector('.collection');
      this.activeSortLoading = { requestId, collection, cacheKey, page };
      collection?.classList.add('loading');
      collection?.setAttribute('aria-busy', 'true');
      this.setSortError(false);

      try {
        const entries = await this.getFilteredProductEntries(cacheKey, grid.dataset.id);
        if (
          requestId !== this.sortRequestId
          || this.currentView !== view
          || this.daysPerWeek !== daysPerWeek
          || priceSort !== this.getActivePriceSort()
          || cacheKey !== this.getSortCacheKey()
          || page !== this.getCurrentPage()
        ) return;

        const pageSize = this.getPageSize();
        const sorted = entries
          .map((entry, index) => ({
            ...entry,
            originalIndex: index,
            calculation: this.calculateComparison(entry.costData, this.currentView)
          }))
          .sort((a, b) => {
            const aValid = Number.isFinite(a.calculation?.periodCostCents);
            const bValid = Number.isFinite(b.calculation?.periodCostCents);
            if (aValid !== bValid) return aValid ? -1 : 1;
            if (!aValid) return a.originalIndex - b.originalIndex;

            const difference = a.calculation.periodCostCents - b.calculation.periodCostCents;
            if (difference !== 0) return priceSort === 'price-ascending' ? difference : -difference;
            return a.originalIndex - b.originalIndex;
          });
        const start = (page - 1) * pageSize;
        const visibleEntries = sorted.slice(start, start + pageSize);
        const fragment = document.createDocumentFragment();

        visibleEntries.forEach((entry) => {
          const template = document.createElement('template');
          template.innerHTML = entry.html.trim();
          const item = template.content.firstElementChild;
          if (!item) return;
          item.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
          item.classList.add('scroll-trigger--cancel');
          fragment.appendChild(item);
        });

        grid.replaceChildren(fragment);
        grid.dataset.contactCostSortSignature = signature;
        this.updateCards(this.currentView);

        const direction = priceSort === 'price-ascending' ? 'low to high' : 'high to low';
        const period = this.currentView === '3_month' ? '3-month' : '6-month';
        this.setText(
          this.querySelector('[data-contact-cost-status]'),
          `Products sorted by ${period} estimated cost, ${direction}.`
        );

        if (typeof window.initializeScrollAnimationTrigger === 'function') {
          try {
            window.initializeScrollAnimationTrigger(gridContainer);
          } catch (error) {
            // Cards remain usable if optional reveal animation setup fails.
          }
        }
      } catch (error) {
        console.warn('Contact lens estimate sorting unavailable', error);
        if (
          requestId === this.sortRequestId
          && this.currentView === view
          && this.daysPerWeek === daysPerWeek
          && priceSort === this.getActivePriceSort()
          && cacheKey === this.getSortCacheKey()
          && page === this.getCurrentPage()
        ) this.setSortError(true);
      } finally {
        if (
          requestId === this.sortRequestId
          && this.currentView === view
          && this.daysPerWeek === daysPerWeek
          && priceSort === this.getActivePriceSort()
          && cacheKey === this.getSortCacheKey()
          && page === this.getCurrentPage()
        ) {
          collection?.classList.remove('loading');
          collection?.removeAttribute('aria-busy');
          if (this.activeSortLoading?.requestId === requestId) this.activeSortLoading = null;
        }
      }
    }

    getPageSize() {
      const select = document.querySelector('[data-city-products-per-page]');
      const selectedLabel = Number(select?.selectedOptions?.[0]?.textContent?.trim());
      if (selectedLabel > 0) return selectedLabel;
      return Math.max(1, document.querySelectorAll('#product-grid > .grid__item').length || 24);
    }

    getFilteredProductEntries(cacheKey, sectionId) {
      if (this.sortDataCache.has(cacheKey)) return this.sortDataCache.get(cacheKey);

      const request = this.fetchFilteredProductEntries(sectionId).catch((error) => {
        this.sortDataCache.delete(cacheKey);
        throw error;
      });
      this.sortDataCache.set(cacheKey, request);
      if (this.sortDataCache.size > 8) this.sortDataCache.delete(this.sortDataCache.keys().next().value);
      return request;
    }

    async fetchFilteredProductEntries(sectionId) {
      const baseUrl = new URL(window.location.href);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      const fetchPage = async (page) => {
        const url = new URL(baseUrl.href);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('sort_by', 'manual');
        url.searchParams.set('page', String(page));

        const response = await fetch(`${url.pathname}?${url.searchParams.toString()}`, {
          credentials: 'same-origin',
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Filtered product request failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = Array.from(html.querySelectorAll('#product-grid > .grid__item'));
        const totalText = html.querySelector('.city-collection-panel-header__summary-total')?.textContent || '';
        const totalMatch = totalText.match(/\d+/);
        if (!totalMatch) throw new Error('Filtered product response was missing its result total');
        const total = Number(totalMatch[0]);
        return { items, total };
      };

      try {
        const currentTotalText = document.querySelector('.city-collection-panel-header__summary-total')?.textContent || '';
        const currentTotal = Number(currentTotalText.match(/\d+/)?.[0]);
        const currentPageSize = this.getPageSize();
        const knownPageCount = currentTotal > 0 ? Math.max(1, Math.ceil(currentTotal / currentPageSize)) : 0;
        let pageResults = [];

        if (knownPageCount) {
          pageResults = await Promise.all(Array.from({ length: knownPageCount }, (_, index) => fetchPage(index + 1)));
          const fetchedPageSize = Math.max(1, pageResults[0]?.items.length || currentPageSize);
          const fetchedTotal = pageResults[0]?.total || currentTotal;
          const fetchedPageCount = Math.max(1, Math.ceil(fetchedTotal / fetchedPageSize));
          if (fetchedPageCount > pageResults.length) {
            const missingPages = await Promise.all(
              Array.from(
                { length: fetchedPageCount - pageResults.length },
                (_, index) => fetchPage(pageResults.length + index + 1)
              )
            );
            pageResults.push(...missingPages);
          }
        } else {
          const firstPage = await fetchPage(1);
          const pageSize = Math.max(1, firstPage.items.length);
          const pageCount = Math.max(1, Math.ceil(firstPage.total / pageSize));
          const remainingPages = pageCount > 1
            ? await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2)))
            : [];
          pageResults = [firstPage, ...remainingPages];
        }

        const reportedTotals = pageResults.map((result) => result.total);
        const expectedTotal = reportedTotals[0];
        if (!Number.isFinite(expectedTotal) || expectedTotal < 0 || reportedTotals.some((total) => total !== expectedTotal)) {
          throw new Error('Filtered product response totals were inconsistent');
        }

        const allItems = pageResults.flatMap((result) => result.items);
        const seen = new Set();
        const entries = allItems.reduce((results, item) => {
          const card = item.querySelector('[data-contact-cost-card]');
          const key = card?.querySelector('.card__heading a')?.getAttribute('href');
          if (!card || !key || seen.has(key)) return results;
          seen.add(key);
          results.push({
            key,
            html: item.outerHTML,
            costData: {
              costComparisonPricePerLens: card.dataset.costComparisonPricePerLens,
              costComparisonSchedule: card.dataset.costComparisonSchedule
            }
          });
          return results;
        }, []);

        if (entries.length !== expectedTotal) {
          throw new Error(`Filtered product response was incomplete (${entries.length} of ${expectedTotal})`);
        }

        return entries;
      } catch (error) {
        controller.abort();
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    }

    setText(element, value) {
      if (element && element.textContent !== value) element.textContent = value;
    }

    setSortError(show) {
      const error = this.querySelector('[data-contact-cost-sort-error]');
      if (error) error.hidden = !show;
    }

    track(event, details = {}) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event, ...details });
    }
  });
}
